import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

// Auto-generate 2-word title from user input
const generateTitle = (userInput) => {
  if (!userInput) return 'Reflection';
  const input = userInput.toLowerCase();
  
  // Theme detection (priority order)
  const themes = [
    [['grief', 'death', 'loss', 'died', 'passing', 'mourning'], 'Grief'],
    [['anxiety', 'anxious', 'nervous', 'panic', 'worry', 'worried'], 'Anxiety'],
    [['depression', 'depressed', 'hopeless', 'empty'], 'Depression'],
    [['anger', 'angry', 'furious', 'rage', 'mad', 'frustrated', 'hate', 'hatred'], 'Anger'],
    [['forgive', 'forgiveness', 'grudge', 'betrayed'], 'Forgiveness'],
    [['fear', 'afraid', 'scared', 'terrified'], 'Fear'],
    [['marriage', 'spouse', 'husband', 'wife', 'divorce'], 'Marriage'],
    [['family', 'parent', 'child', 'kids', 'son', 'daughter', 'mother', 'father', 'mom', 'dad'], 'Family'],
    [['work', 'job', 'career', 'boss', 'coworker', 'office', 'fired'], 'Work'],
    [['money', 'financial', 'debt', 'bills', 'broke', 'budget'], 'Financial'],
    [['health', 'sick', 'illness', 'disease', 'cancer', 'hospital'], 'Health'],
    [['faith', 'believe', 'god', 'prayer', 'spiritual', 'church', 'jesus'], 'Faith'],
    [['relationship', 'friend', 'lonely', 'alone'], 'Relationship'],
    [['love', 'dating', 'heartbreak', 'breakup'], 'Love'],
    [['purpose', 'meaning', 'direction', 'calling'], 'Purpose'],
    [['decision', 'choice', 'confused', 'uncertain'], 'Decision'],
    [['addiction', 'drinking', 'alcohol', 'drugs'], 'Addiction'],
    [['leadership', 'lead', 'leader', 'team'], 'Leadership'],
    [['peace', 'calm', 'rest', 'overwhelmed', 'stress'], 'Peace'],
    [['politics', 'trump', 'vance', 'biden', 'election', 'government'], 'Politics'],
    [['food', 'eat', 'eating', 'diet', 'corn', 'bread'], 'Food'],
  ];
  
  // State detection
  const states = [
    [['struggle', 'struggling', 'hard', 'difficult', 'challenge'], 'Struggle'],
    [['journey', 'path', 'process', 'walk'], 'Journey'],
    [['crisis', 'emergency', 'urgent', 'desperate'], 'Crisis'],
    [['healing', 'heal', 'recover', 'recovery'], 'Healing'],
    [['waiting', 'wait', 'patience', 'patient'], 'Waiting'],
    [['transition', 'moving', 'change', 'new', 'season'], 'Transition'],
    [['growth', 'grow', 'growing', 'learning'], 'Growth'],
    [['burden', 'heavy', 'weight', 'tired', 'exhausted'], 'Burden'],
    [['hope', 'hopeful', 'future', 'better'], 'Hope'],
    [['doubt', 'uncertain', 'questioning'], 'Doubt'],
    [['victory', 'overcome', 'won', 'success'], 'Victory'],
    [['lost', 'confused', 'searching'], 'Search'],
    [['think', 'thinking', 'thought', 'thoughts'], 'Thoughts'],
    [['evil', 'bad', 'wrong', 'hate'], 'Concern'],
  ];
  
  let theme = 'Life';
  for (const [keywords, name] of themes) {
    if (keywords.some(kw => input.includes(kw))) {
      theme = name;
      break;
    }
  }
  
  let state = 'Moment';
  for (const [keywords, name] of states) {
    if (keywords.some(kw => input.includes(kw))) {
      state = name;
      break;
    }
  }
  
  return `${theme} ${state}`;
};

const ReflectionHistory = () => {
  const navigate = useNavigate();
  const { isPremium, user } = useAuth();
  const { t, isSpanish } = useLanguage();
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all, saved
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    loadReflections();
  }, [currentPage]);

  const loadReflections = async () => {
    setLoading(true);
    try {
      const data = await api.getReflectionHistory(itemsPerPage, (currentPage - 1) * itemsPerPage);
      setReflections(data.reflections || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error loading reflections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    // Prevent navigation to reflection detail
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const confirmMsg = isSpanish 
      ? '¿Estás seguro de que quieres eliminar esta reflexión?' 
      : 'Are you sure you want to delete this reflection?';
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await api.deleteReflection(id);
      setReflections(prev => prev.filter(r => r.id !== id));
      setTotal(prev => prev - 1);
    } catch (error) {
      console.error('Error deleting reflection:', error);
      const errorMsg = isSpanish 
        ? 'Error al eliminar la reflexión. Por favor intenta de nuevo.'
        : 'Failed to delete reflection. Please try again.';
      alert(errorMsg);
    }
  };

  // Direct delete without confirmation (confirmation handled in onClick)
  const handleDeleteDirect = async (id) => {
    try {
      await api.deleteReflection(id);
      setReflections(prev => prev.filter(r => r.id !== id));
      setTotal(prev => prev - 1);
    } catch (error) {
      console.error('Error deleting reflection:', error);
      const errorMsg = isSpanish 
        ? 'Error al eliminar la reflexión. Por favor intenta de nuevo.'
        : 'Failed to delete reflection. Please try again.';
      alert(errorMsg);
    }
  };

  const filteredReflections = reflections.filter(r => {
    const matchesSearch = searchQuery === '' || 
      r.user_input?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.response?.verse?.reference?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filter === 'all' || (filter === 'saved' && r.is_saved);
    
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(total / itemsPerPage);

  // Check if subscription is ending soon
  const subscriptionEndDate = user?.subscription_end_date ? new Date(user.subscription_end_date) : null;
  const daysUntilRenewal = subscriptionEndDate 
    ? Math.ceil((subscriptionEndDate - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const showRenewalNotice = isPremium && daysUntilRenewal && daysUntilRenewal <= 3;

  return (
    <>
      <Helmet>
        <title>{t('goldMineTitle')} - Lumiveta</title>
        <meta name="description" content={t('goldMineSubtitle')} />
      </Helmet>

      <div className="min-h-dvh bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h1 className="font-heading font-bold text-3xl lg:text-4xl text-gold-gradient mb-2">
                      {t('goldMineTitle')}
                    </h1>
                    <p className="text-muted-foreground">
                      {t('goldMineSubtitle')}
                    </p>
                  </div>
                  <Button
                    variant="default"
                    onClick={() => navigate('/main-dashboard')}
                    className="hidden sm:flex gold-glow"
                  >
                    <Icon name="Plus" size={18} className="mr-2" />
                    {isSpanish ? 'Nueva Reflexión' : 'New Reflection'}
                  </Button>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Gem" size={16} color="#FFD700" />
                  <span>{total} {isSpanish ? 'reflexiones totales' : 'total reflections'}</span>
                  {isPremium && (
                    <>
                      <span className="text-border">•</span>
                      <div className="flex items-center gap-1">
                        <Icon name="Crown" size={14} color="#FFD700" />
                        <span className="text-primary">{t('premiumMember')}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Renewal Notice */}
              {showRenewalNotice && (
                <div className="mb-6 p-4 glass border border-warning/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Icon name="Clock" size={20} color="#FFD700" />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">
                        <strong>{isSpanish ? '¡Renovación próxima!' : 'Renewing Soon!'}</strong> {isSpanish ? `Tu suscripción se renueva en ${daysUntilRenewal} día${daysUntilRenewal !== 1 ? 's' : ''}.` : `Your subscription renews in ${daysUntilRenewal} day${daysUntilRenewal !== 1 ? 's' : ''}.`}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/account-settings')}
                    >
                      {isSpanish ? 'Gestionar' : 'Manage'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Search & Filters */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Icon 
                    name="Search" 
                    size={18} 
                    color="#94A3B8" 
                    className="absolute left-3 top-1/2 -translate-y-1/2" 
                  />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('searchPlaceholder')}
                    className="pl-10 bg-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === 'all'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {isSpanish ? 'Todos' : 'All'}
                  </button>
                  <button
                    onClick={() => setFilter('saved')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === 'saved'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Bookmark" size={14} className="inline mr-1" />
                    {t('saved')}
                  </button>
                </div>
              </div>

              {/* Reflections List */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">{isSpanish ? 'Cargando tu oro...' : 'Loading your gold...'}</p>
                </div>
              ) : filteredReflections.length === 0 ? (
                <div className="text-center py-12 glass rounded-2xl">
                  <Icon name="Inbox" size={48} color="#94A3B8" className="mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    {searchQuery || filter !== 'all' 
                      ? (isSpanish ? 'No se encontraron reflexiones' : 'No reflections found')
                      : t('noReflections')}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {searchQuery || filter !== 'all' 
                      ? (isSpanish ? 'Intenta ajustar tu búsqueda o filtros' : 'Try adjusting your search or filters')
                      : t('startJourney')}
                  </p>
                  {!searchQuery && filter === 'all' && (
                    <Button
                      variant="default"
                      onClick={() => navigate('/main-dashboard')}
                      className="gold-glow"
                    >
                      <Icon name="Plus" size={18} className="mr-2" />
                      {isSpanish ? 'Crear Primera Reflexión' : 'Create First Reflection'}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredReflections.map((reflection) => (
                    <div
                      key={reflection.id}
                      className="bento-item group relative overflow-visible"
                    >
                      {/* Clickable area for navigation */}
                      <div 
                        className="cursor-pointer pr-14"
                        onClick={() => navigate('/reflection-response', { state: { reflectionData: reflection } })}
                      >
                        <div className="flex-1 min-w-0">
                          {/* Title - Auto-generated 2-word summary */}
                          <h3 className="font-semibold text-foreground text-lg mb-1">
                            {reflection.title || generateTitle(reflection.user_input)}
                          </h3>
                          
                          {/* Date - Secondary below title */}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                            <Icon name="Calendar" size={12} />
                            {new Date(reflection.created_at).toLocaleDateString(isSpanish ? 'es-ES' : 'en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                            {reflection.is_saved && (
                              <span className="flex items-center gap-1 text-primary bg-primary/10 px-2 py-1 rounded">
                                <Icon name="Bookmark" size={12} />
                                {t('saved')}
                              </span>
                            )}
                          </div>
                          
                          {/* Preview text */}
                          <p className="text-muted-foreground line-clamp-2 mb-2">
                            {reflection.user_input}
                          </p>
                          
                          {reflection.response?.verse && (
                            <p className="text-sm text-muted-foreground">
                              <Icon name="Book" size={12} className="inline mr-1" />
                              {reflection.response.verse.reference}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Delete button - completely separate, uses onPointerDown for mobile */}
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 right-3 z-50"
                        style={{ pointerEvents: 'auto' }}
                      >
                        <button
                          type="button"
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            e.nativeEvent.stopImmediatePropagation();
                            const confirmMsg = isSpanish ? '¿Eliminar esta reflexión?' : 'Delete this reflection?';
                            if (window.confirm(confirmMsg)) {
                              handleDeleteDirect(reflection.id);
                            }
                          }}
                          className="p-3 bg-background border border-border text-muted-foreground hover:text-error hover:bg-error/10 hover:border-error/30 rounded-full shadow-md transition-all"
                          title={isSpanish ? 'Eliminar reflexión' : 'Delete reflection'}
                          aria-label={isSpanish ? 'Eliminar reflexión' : 'Delete reflection'}
                          data-testid={`delete-reflection-${reflection.id}`}
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </Button>
                  
                  <span className="px-4 text-sm text-muted-foreground">
                    {isSpanish ? `Página ${currentPage} de ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <Icon name="ChevronRight" size={16} />
                  </Button>
                </div>
              )}

              {/* Mobile New Reflection Button */}
              <Button
                variant="default"
                fullWidth
                onClick={() => navigate('/main-dashboard')}
                className="sm:hidden mt-6 gold-glow"
              >
                <Icon name="Plus" size={18} className="mr-2" />
                {isSpanish ? 'Nueva Reflexión' : 'New Reflection'}
              </Button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-border py-6">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm text-muted-foreground">
              © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ReflectionHistory;
